
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { RegionDialog } from "./RegionDialog";
import { DeleteConfirmationDialog } from "@/components/DeleteConfirmationDialog";

export const Regions = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<{ id: string; name: string } | null>(null);
  const queryClient = useQueryClient();

  const { data: regions, isLoading } = useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  const handleEdit = (region: { id: string; name: string }) => {
    setSelectedRegion(region);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedRegion) return;

    try {
      const { error } = await supabase
        .from("regions")
        .delete()
        .eq("id", selectedRegion.id);

      if (error) throw error;

      toast.success("Region deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["regions"] });
      setIsDeleteDialogOpen(false);
      setSelectedRegion(null);
    } catch (error) {
      console.error("Error deleting region:", error);
      toast.error("Failed to delete region");
    }
  };

  const confirmDelete = (region: { id: string; name: string }) => {
    setSelectedRegion(region);
    setIsDeleteDialogOpen(true);
  };

  if (isLoading) {
    return <div>Loading regions...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => {
          setSelectedRegion(null);
          setIsDialogOpen(true);
        }}>
          <Plus className="mr-2 h-4 w-4" />
          Add Region
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {regions?.map((region) => (
          <div key={region.id} className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{region.name}</h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleEdit(region)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => confirmDelete(region)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <RegionDialog 
        open={isDialogOpen} 
        onOpenChange={setIsDialogOpen}
        regionToEdit={selectedRegion}
      />

      <DeleteConfirmationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Region"
        description={`Are you sure you want to delete ${selectedRegion?.name}? This action will also delete all dependent organizations and sites. This action cannot be undone.`}
      />
    </div>
  );
};
